import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { cloneDeep } from 'lodash';
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { UserSettingsService } from 'src/app/settings/service/user-settings.service';
import { Device } from 'src/app/vehicles/model/device';
import { DeviceType } from 'src/app/vehicles/model/device-type';
import { AdditionalInfoImpl } from 'src/app/vehicles/model/status/additional-info-impl';
import { Vehicle, VehicleProperty } from 'src/app/vehicles/model/vehicle';
import { VehicleDeviceLocation } from 'src/app/vehicles/model/vehicle-device-location';
import { VehicleLayout } from 'src/app/vehicles/model/vehicle-layout';
import { VehiclesService } from 'src/app/vehicles/service/vehicles.service';
import { DeviceViewInfoHolder } from '../../model/device-view';
import { DeviceFilterComponent } from './device-filter/device-filter.component';

@Component({
  selector: 'app-vehicle-layout-view',
  templateUrl: './vehicle-layout-view.component.html',
  styleUrls: ['./vehicle-layout-view.component.scss']
})
export class VehicleLayoutViewComponent implements OnInit {

  @ViewChild('filter') filter: DeviceFilterComponent;

  @Output() editLocation: EventEmitter<number | string> = new EventEmitter<number>();
  @Output() devicesDropped: EventEmitter<CdkDragDrop<string>> = new EventEmitter<CdkDragDrop<string>>();

  @Input() set additionalInfoImpls(additionalInfoImpls: AdditionalInfoImpl[]) {
    if (additionalInfoImpls)
      this.deviceViews = this.getViews(additionalInfoImpls);
  }
  @Input() editableMode: boolean = true;
  @Input() singleLocation: boolean = false;
  @Input() vehicle: Vehicle;
  @Input() configurationMode: boolean = false;

  isClicked = false;
  layout: VehicleLayout;
  layoutSubject = new Subject<VehicleLayout>();
  showLayout: boolean = false;
  deviceViews: DeviceViewInfoHolder[] = [];
  displayedLocations: VehicleDeviceLocation[] = [];


  constructor(private vehiclesService: VehiclesService, private userSettingsService: UserSettingsService) {
  }

  ngOnInit(): void {
    if (!this.configurationMode && (this.singleLocation || !this.editableMode))
      this.init();
  }

  private init(): void {
    this.showLayout = false;
    if (this.vehicle && (!this.editableMode || this.vehicle.properties[VehicleProperty.VEHICLE_LAYOUT_TYPE])
      && this.userSettingsService.iSVehicleLayoutEnabled()) {
      this.loadLayout(this.vehicle.sideNumber).pipe(map((layout: VehicleLayout) => {
        this.layout = layout;
        if (this.deviceViews.length === 0 && this.layout) {
          this.loadDevices().pipe(map((devices: Device[]) => devices.forEach((device: Device) => {
            this.deviceViews.push(
              {
                device,
                error: false
              }
            );
          }))).subscribe();
        } else if (this.singleLocation && this.layout) {
          this.displayCurrentLocation();
        };
      })).subscribe(() => this.layoutSubject.next(this.layout));
    } else {
      this.layout = null;
      this.layoutSubject.next(this.layout);
    }
  }

  private generateDisplayedLocations(): void {
    this.displayedLocations = cloneDeep(this.layout.deviceLocations);
  }

  private loadLayout(sideNumber: string): Observable<VehicleLayout> {
    return this.vehiclesService.getVehicleLayout(sideNumber);
  }

  private loadDevices(): Observable<Device[]> {
    return this.vehiclesService.getAllVehicleDevices(this.vehicle.sideNumber);
  }

  private getViews(additionalInfoImpls: AdditionalInfoImpl[]): DeviceViewInfoHolder[] {
    const devicesViews: DeviceViewInfoHolder[] = additionalInfoImpls.map(additionalInfoImpl => (
      {
        device: additionalInfoImpl.device,
        error: additionalInfoImpl.error
      }));
    return devicesViews;
  }

  onClicked(event: VehicleDeviceLocation): void {
    if (this.configurationMode)
      this.editLocation.emit(event.locationName);
    else if (this.editableMode && this.deviceViews.length !== 0) {
      this.isClicked = true;
      this.editLocation.emit(event.locationId);
    }
  }

  initLayout(vehicle: Vehicle): void {
    this.vehicle = vehicle;
    if (this.layout)
      this.layout = null;
    this.init();
  }

  initConfig(vehicleLayout: VehicleLayout, clearView: boolean): void {
    if (clearView)
      this.deviceViews = [];
    this.layout = vehicleLayout;
    this.displayedLocations = this.layout.deviceLocations;
    this.showLayout = true;
    setTimeout(() => this.filter.refresh());
  }

  destroy(): void {
    this.layout = null;
    this.vehicle = null;
    this.deviceViews = [];
  }

  checkError(device: Device): string {
    const deviceViews = this.deviceViews.find((data: DeviceViewInfoHolder) => data.device.devId === device.devId);
    if (deviceViews?.error) {
      return 'with-error';
    }
    return 'no-error';
  }

  filterDisplayedDevices(event: VehicleDeviceLocation[]): void {
    this.displayedLocations = event;
  }

  displayCurrentLocation(): void {
    const locations = this.layout.deviceLocations = this.layout.deviceLocations
      .filter(loc => loc.devices.map(d => d.devId)
        .includes(this.deviceViews.find((_v, idx) => idx === 0).device.devId));
    const devices = locations
      .find((_v, idx) => idx === 0)?.devices.filter(dev => dev.devId === this.deviceViews.find((_v, idx) => idx === 0).device.devId);
    if (devices)
      this.layout.deviceLocations.find((_loc, idx) => idx === 0).devices = devices;
    else this.layout = null;
  }

  updateLocations(locations: VehicleDeviceLocation[]): void {
    this.layout.deviceLocations = locations;
    this.generateDisplayedLocations();
    if (this.filter)
      setTimeout(() => this.filter.refresh());
  }

  open(): void {
    this.generateDisplayedLocations();
    this.showLayout = true;
  }

  dropDevice(event: CdkDragDrop<string>): void {
    this.devicesDropped.emit(event);
  }

  findDeviceKey(deviceType: DeviceType, index: number, locationName: string): string {
    const deviceKey = this.deviceViews
      .find((view: DeviceViewInfoHolder) => view.device.deviceType === deviceType && view.index == index && view.locationName == locationName)?.deviceKey;
    return deviceKey ? deviceKey : deviceType;
  }

  addDeviceViewWithIndex(device: Device, deviceKey: string, index: number, locationName: string): void {
    this.deviceViews.push({
      device,
      error: false,
      deviceKey,
      locationName,
      index
    });
  }

  deleteDeviceViewWithIndex(index: number, locationName: string): void {
    const idx: number = this.deviceViews.findIndex((view: DeviceViewInfoHolder) => view.locationName == locationName && view.index == index);
    this.deviceViews.splice(idx, 1);
    this.deviceViews.map((view: DeviceViewInfoHolder) => {
      if (view.index > index)
        view.index--;
    });
  };
}
